// src/components/FrameworkSuggestionViewer.js
import { MluLink } from "./common/MluDetails";
import React from "react";
import {
  Paper,
  Stack,
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LinkIcon from "@mui/icons-material/Link";
import AiBorderWrapper from "./AIBorderWrapper";
import { groupIntoPairs } from "../utils/mluPairingUtils";

export default function FrameworkSuggestionViewer({
  label,
  codePrefix,
  suggestions = [],
  shortlist = [],
  bgColor,
  imageSrc,
  certificationNeeded = false,
}) {
  const [open, setOpen] = React.useState(false);
  const upperPrefix = (codePrefix || "").toUpperCase();

  // Dark main colors for the counter (same logic as FrameworkSelector)
  const MAIN_COLORS = {
    EDU: "#c62828", // red
    GOV: "#2e7d32", // green
    TEC: "#1565c0", // blue
    EID: "#f9a825", // gold
  };

  const badgeColor = MAIN_COLORS[upperPrefix] || "#424242";
  const counterValue = Array.isArray(suggestions) ? suggestions.length : 0;

  // Collapsed summary: only show MLU codes (no titles)
  const collapsedList = React.useMemo(() => {
    if (!Array.isArray(suggestions) || suggestions.length === 0) {
      return "None";
    }

    const codes = suggestions
      .map((s) => {
        // Object shape { id, title, ... }
        if (s && typeof s === "object") {
          return (s.id || "").trim();
        }
        // Plain string ID
        if (typeof s === "string") {
          return s.trim();
        }
        return "";
      })
      .filter(Boolean);

    return codes.length > 0 ? codes.join(", ") : "None";
  }, [suggestions]);

  // Render a single shortlist card
  const renderCard = (item) => (
    <Paper
      key={item.id}
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "#fff",
        mb: 1,
      }}
    >
      <Typography variant="body2">
        <strong><MluLink id={item.id} /></strong> · {item.title || ""}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5 }}
      >
        {item.why}
      </Typography>
    </Paper>
  );

  // Render shortlist items — grouped in pairs when certification is needed
  const renderShortlistContent = () => {
    if (!shortlist || shortlist.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          No detailed suggestions available for this framework.
        </Typography>
      );
    }

    if (certificationNeeded) {
      const pairs = groupIntoPairs(shortlist);
      return pairs.map((group) => {
        if (group.length === 2) {
          return (
            <Box
              key={`pair-${group[0].id}`}
              sx={{
                border: "2px dashed",
                borderColor: "primary.light",
                borderRadius: 2,
                p: 1,
                mb: 1.5,
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                position: "relative",
              }}
            >
              {/* Link badge */}
              <Box sx={{
                position: "absolute",
                top: -10,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "primary.main",
                color: "white",
                borderRadius: "50%",
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}>
                <LinkIcon sx={{ fontSize: 14 }} />
              </Box>
              {group.map(renderCard)}
            </Box>
          );
        }
        // Unpaired MLU — render normally
        return renderCard(group[0]);
      });
    }

    // Default: render each item individually
    return shortlist.map(renderCard);
  };

  return (
    <AiBorderWrapper aiActive={true} animated={false} sx={{ mb: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          border: "none",          // ✅ border handled by AiBorderWrapper
          backgroundColor: bgColor,
        }}
      >
        {/* Header row */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1">{label}</Typography>

            {!open && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                <strong>T4R LJD Agent Suggested Units:</strong>{" "}
                {collapsedList}
                {certificationNeeded && " · 🔗 Certification mode"}
              </Typography>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
            {/* Counter circle */}
            <Box
              sx={{
                minWidth: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: badgeColor,
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {counterValue}
            </Box>

            {/* Framework image + expand/collapse */}
            <Box
              component="img"
              src={imageSrc}
              alt={`${upperPrefix} icon`}
              sx={{
                width: open ? 40 : 72,
                height: open ? 40 : 72,
                borderRadius: 1,
                objectFit: "cover",
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpen((o) => !o)}
              endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                textTransform: "none",
                ml: 1,
                whiteSpace: "nowrap",
                borderRadius: 2,
                fontWeight: 600,
                borderColor: "divider",
                color: "text.primary"
              }}
            >
              {open ? "Hide MLUs" : "Show MLUs"}
            </Button>
          </Box>
        </Stack>

        {/* Expanded content */}
        {open && (
          <Box mt={2}>
            {certificationNeeded && shortlist && shortlist.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1.5 }}>
                🔗 Certification mode: paired MLUs are grouped together.
              </Typography>
            )}
            {renderShortlistContent()}
          </Box>
        )}
      </Paper>
    </AiBorderWrapper>
  );
}
